import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FieldArray, getIn } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
  FormControlLabel,
} from "@material-ui/core";
import { Colorize } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const WEBHOOK_EVENT_OPTIONS = [
  {
    value: "message_received",
    labelKey: "queueModal.integrations.events.messageReceived",
  },
  {
    value: "message_sent",
    labelKey: "queueModal.integrations.events.messageSent",
  },
  {
    value: "conversation_started",
    labelKey: "queueModal.integrations.events.conversationStarted",
  },
  {
    value: "conversation_finished",
    labelKey: "queueModal.integrations.events.conversationFinished",
  },
];

const FLOWCH_SOURCE_OPTIONS = [
  { value: "payload.ticketId", labelKey: "queueModal.integrations.flowch.sources.ticketId" },
  { value: "payload.messageId", labelKey: "queueModal.integrations.flowch.sources.messageId" },
  { value: "payload.contactId", labelKey: "queueModal.integrations.flowch.sources.contactId" },
  { value: "payload.contactNumber", labelKey: "queueModal.integrations.flowch.sources.contactNumber" },
  { value: "payload.contactLid", labelKey: "queueModal.integrations.flowch.sources.contactLid" },
  { value: "payload.contactIsGroup", labelKey: "queueModal.integrations.flowch.sources.contactIsGroup" },
  { value: "payload.messageContactId", labelKey: "queueModal.integrations.flowch.sources.messageContactId" },
  { value: "payload.messageContactName", labelKey: "queueModal.integrations.flowch.sources.messageContactName" },
  { value: "payload.messageContactNumber", labelKey: "queueModal.integrations.flowch.sources.messageContactNumber" },
  { value: "payload.participantContactId", labelKey: "queueModal.integrations.flowch.sources.participantContactId" },
  { value: "payload.participantName", labelKey: "queueModal.integrations.flowch.sources.participantName" },
  { value: "payload.participantNumber", labelKey: "queueModal.integrations.flowch.sources.participantNumber" },
  { value: "payload.participantLid", labelKey: "queueModal.integrations.flowch.sources.participantLid" },
  { value: "payload.groupContactId", labelKey: "queueModal.integrations.flowch.sources.groupContactId" },
  { value: "payload.groupName", labelKey: "queueModal.integrations.flowch.sources.groupName" },
  { value: "payload.groupNumber", labelKey: "queueModal.integrations.flowch.sources.groupNumber" },
  { value: "payload.body", labelKey: "queueModal.integrations.flowch.sources.messageBody" },
  { value: "payload.fromMe", labelKey: "queueModal.integrations.flowch.sources.fromMe" },
  { value: "payload.mediaType", labelKey: "queueModal.integrations.flowch.sources.mediaType" },
  { value: "payload.ticketStatus", labelKey: "queueModal.integrations.flowch.sources.ticketStatus" },
  { value: "payload.userId", labelKey: "queueModal.integrations.flowch.sources.userId" },
  { value: "payload.whatsappId", labelKey: "queueModal.integrations.flowch.sources.whatsappId" },
  { value: "payload.whatsappName", labelKey: "queueModal.integrations.flowch.sources.whatsappName" },
  { value: "payload.connectionId", labelKey: "queueModal.integrations.flowch.sources.connectionId" },
  { value: "payload.connectionName", labelKey: "queueModal.integrations.flowch.sources.connectionName" },
  { value: "payload.channel", labelKey: "queueModal.integrations.flowch.sources.channel" },
  { value: "payload.provider", labelKey: "queueModal.integrations.flowch.sources.provider" },
  { value: "context.event", labelKey: "queueModal.integrations.flowch.sources.event" },
  { value: "context.queueId", labelKey: "queueModal.integrations.flowch.sources.queueId" },
  { value: "context.timestamp", labelKey: "queueModal.integrations.flowch.sources.timestamp" },
  { value: "fixed", labelKey: "queueModal.integrations.flowch.sources.fixed" },
];

const FLOWCH_TALK_SOURCE_OPTIONS = FLOWCH_SOURCE_OPTIONS.filter(
  option => option.value !== "fixed"
);

const DEFAULT_FLOWCH_MAPPING = {
  fieldName: "",
  label: "",
  type: "",
  required: false,
  send: true,
  source: "payload.body",
  fixedValue: "",
};

const DEFAULT_INTEGRATION = {
  type: "webhook",
  name: "",
  webhookUrl: "",
  endpointUrl: "",
  flowchDocUrl: "",
  flowchFields: [],
  flowchMappings: [],
  triggerEvents: [],
  contactScope: "all",
  contactIds: [],
  token: "",
  status: "active",
};

const TEMPLATE_LANGUAGE_OPTIONS = [
  { value: "pt_BR", label: "Portuguese (Brazil) - pt_BR" },
  { value: "en_US", label: "English (US) - en_US" },
  { value: "es_ES", label: "Spanish (Spain) - es_ES" },
];

const parseTemplateNamesCsv = value =>
  String(value || "")
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);

const normalizeToStringArray = value => {
  if (Array.isArray(value)) {
    return value.map(item => String(item || "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map(item => item.trim())
      .filter(Boolean);
  }

  if (value && typeof value === "object" && Symbol.iterator in value) {
    return Array.from(value)
      .map(item => String(item || "").trim())
      .filter(Boolean);
  }

  return [];
};

const dedupeStringArray = value => Array.from(new Set(normalizeToStringArray(value)));

const parseTemplateRules = value => {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return {
      global: [],
      byWhatsappId: {},
    };
  }

  if (rawValue.startsWith("{") && rawValue.endsWith("}")) {
    try {
      const parsed = JSON.parse(rawValue);
      const global = Array.isArray(parsed?.global)
        ? parsed.global.map(item => String(item || "").trim()).filter(Boolean)
        : [];

      const byWhatsappId = Object.entries(parsed?.byWhatsappId || {}).reduce(
        (acc, [whatsappId, items]) => {
          const normalizedItems = Array.isArray(items)
            ? items.map(item => String(item || "").trim()).filter(Boolean)
            : [];

          if (normalizedItems.length) {
            acc[String(whatsappId)] = normalizedItems;
          }

          return acc;
        },
        {}
      );

      return {
        global,
        byWhatsappId,
        byOfficialConnectionId: Object.entries(parsed?.byOfficialConnectionId || {}).reduce(
          (acc, [connectionId, items]) => {
            const normalizedItems = Array.isArray(items)
              ? items.map(item => String(item || "").trim()).filter(Boolean)
              : [];

            if (normalizedItems.length) {
              acc[String(connectionId)] = normalizedItems;
            }

            return acc;
          },
          {}
        ),
      };
    } catch (_err) {
      // Fallback to legacy CSV format below.
    }
  }

  return {
    global: parseTemplateNamesCsv(rawValue),
    byWhatsappId: {},
    byOfficialConnectionId: {},
  };
};

const serializeTemplateRules = rules => {
  const global = Array.isArray(rules?.global)
    ? rules.global.map(item => String(item || "").trim()).filter(Boolean)
    : [];

  const byWhatsappId = Object.entries(rules?.byWhatsappId || {}).reduce(
    (acc, [whatsappId, items]) => {
      const normalizedItems = Array.isArray(items)
        ? items.map(item => String(item || "").trim()).filter(Boolean)
        : [];

      if (normalizedItems.length) {
        acc[String(whatsappId)] = dedupeStringArray(normalizedItems);
      }

      return acc;
    },
    {}
  );

  const byOfficialConnectionId = Object.entries(
    rules?.byOfficialConnectionId || {}
  ).reduce(
    (acc, [connectionId, items]) => {
      const normalizedItems = Array.isArray(items)
        ? items.map(item => String(item || "").trim()).filter(Boolean)
        : [];

      if (normalizedItems.length) {
        acc[String(connectionId)] = dedupeStringArray(normalizedItems);
      }

      return acc;
    },
    {}
  );

  if (!Object.keys(byWhatsappId).length && !Object.keys(byOfficialConnectionId).length) {
    return serializeTemplateNamesCsv(global);
  }

  return JSON.stringify({
    global: dedupeStringArray(global),
    byWhatsappId,
    byOfficialConnectionId,
  });
};

const serializeTemplateNamesCsv = values => {
  if (!Array.isArray(values)) {
    return "";
  }

  return values
    .map(item => String(item || "").trim())
    .filter(Boolean)
    .join(", ");
};

const normalizeSearchText = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const onlyDigits = value => String(value || "").replace(/\D/g, "");

const formatContactLabel = contact => {
  const name = String(contact?.name || "").trim();
  const number = String(contact?.number || "").trim();

  if (name && number) {
    const nameDigits = onlyDigits(name);
    const numberDigits = onlyDigits(number);

    // Avoid duplicated number when the contact name already contains it.
    if (numberDigits && nameDigits.includes(numberDigits)) {
      return name;
    }

    return `${name} - ${number}`;
  }

  if (name) return name;
  if (number) return number;
  return `#${contact?.id || ""}`;
};

const sortContacts = contacts => {
  return [...contacts].sort((a, b) => {
    const nameA = String(a?.name || "").trim();
    const nameB = String(b?.name || "").trim();
    const hasNameA = Boolean(nameA);
    const hasNameB = Boolean(nameB);

    if (hasNameA !== hasNameB) {
      return hasNameA ? -1 : 1;
    }

    if (hasNameA && hasNameB) {
      return normalizeSearchText(nameA).localeCompare(normalizeSearchText(nameB));
    }

    const numberA = onlyDigits(a?.number);
    const numberB = onlyDigits(b?.number);

    return numberA.localeCompare(numberB);
  });
};

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  basicDataRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginBottom: theme.spacing(0.5),
    "& > *": {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  nameField: {
    flex: 2,
    marginRight: 0,
  },
  colorField: {
    flex: 1,
    marginRight: 0,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  actionButton: {
    borderRadius: 999,
    boxShadow: "none",
    textTransform: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  integrationItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  integrationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  integrationFields: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1),
  },
  addIntegrationBtn: {
    marginTop: theme.spacing(1),
  },
  emptyIntegrations: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  testActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
  },
  testResultBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.default,
  },
  testResultTitle: {
    marginBottom: theme.spacing(1),
  },
  testResultSubtitle: {
    display: "block",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  testResultPre: {
    margin: 0,
    maxHeight: 220,
    overflow: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 12,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
}));

function TabPanel({ children, value, index }) {
  return <div hidden={value !== index}>{value === index && <Box>{children}</Box>}</div>;
}

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
  greetingMessage: Yup.string().nullable(),
  startAttendanceMessage: Yup.string().nullable(),
  finishAttendanceMessage: Yup.string().nullable(),
  queueIntegrations: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required("Required"),
      name: Yup.string().when("type", {
        is: type => type === "webhook" || type === "flowch",
        then: schema => schema.required("Required"),
        otherwise: schema => schema,
      }),
      webhookUrl: Yup.string().when("type", {
        is: "webhook",
        then: schema => schema.url("Invalid URL").required("Required"),
        otherwise: schema => schema,
      }),
      triggerEvents: Yup.array().when("type", {
        is: "webhook",
        then: schema => schema.min(1, "Required").required("Required"),
        otherwise: schema => schema,
      }),
      contactScope: Yup.string().when("type", {
        is: "webhook",
        then: schema => schema.oneOf(["all", "specific"]).required("Required"),
        otherwise: schema => schema,
      }),
      contactIds: Yup.array().when(["type", "contactScope"], {
        is: (type, contactScope) => type === "webhook" && contactScope === "specific",
        then: schema => schema.min(1, "Required").required("Required"),
        otherwise: schema => schema,
      }),
      token: Yup.string(),
      endpointUrl: Yup.string().when("type", {
        is: "flowch",
        then: schema => schema.url("Invalid URL").required("Required"),
        otherwise: schema => schema,
      }),
      flowchMappings: Yup.array().when("type", {
        is: "flowch",
        then: schema =>
          schema
            .of(
              Yup.object().shape({
                fieldName: Yup.string().required("Required"),
                required: Yup.boolean(),
                source: Yup.string().required("Required"),
                fixedValue: Yup.string().when("source", {
                  is: source => source === "fixed",
                  then: fixedSchema => fixedSchema.required("Required"),
                  otherwise: fixedSchema => fixedSchema,
                }),
              })
            )
            .min(1, "Required")
            .required("Required"),
        otherwise: schema => schema,
      }),
      status: Yup.string().oneOf(["active", "inactive"]).required("Required"),
    })
  ),
});

const mergeFlowchMappings = (currentMappings = [], loadedFields = []) => {
  const currentMap = new Map(
    currentMappings.map(mapping => [String(mapping.fieldName || "").trim(), mapping])
  );

  const merged = loadedFields
    .map(field => {
      const fieldName = String(field?.name || "").trim();

      if (!fieldName) {
        return null;
      }

      const existing = currentMap.get(fieldName);

      return {
        ...DEFAULT_FLOWCH_MAPPING,
        ...existing,
        fieldName,
        label: String(field?.label || existing?.label || ""),
        type: String(field?.type || existing?.type || ""),
        required: Boolean(field?.required || existing?.required),
        // Mapping rows are always sent; users remove the row when they don't want it.
        send: true,
        source: String(existing?.source || "").trim() || "payload.body",
      };
    })
    .filter(Boolean);

  currentMappings.forEach(mapping => {
    const fieldName = String(mapping?.fieldName || "").trim();

    if (!fieldName) {
      return;
    }

    const existsInMerged = merged.some(item => item.fieldName === fieldName);

    if (!existsInMerged) {
      merged.push({
        ...DEFAULT_FLOWCH_MAPPING,
        ...mapping,
        fieldName,
      });
    }
  });

  return merged;
};

const normalizeQueueIntegrations = integrations => {
  if (!Array.isArray(integrations)) return [];

  return integrations.map(integration => ({
    ...DEFAULT_INTEGRATION,
    ...integration,
    webhookUrl: integration.webhookUrl || integration?.data?.webhookUrl || "",
    triggerEvents: Array.isArray(integration.triggerEvents)
      ? integration.triggerEvents
      : Array.isArray(integration.events)
        ? integration.events
        : [],
    contactScope:
      integration.contactScope || integration?.data?.contactScope || "all",
    contactIds: Array.isArray(integration.contactIds)
      ? integration.contactIds
      : Array.isArray(integration?.data?.contactIds)
        ? integration.data.contactIds
        : [],
    endpointUrl: integration.endpointUrl || integration?.data?.endpointUrl || "",
    flowchDocUrl: integration.flowchDocUrl || integration?.data?.flowchDocUrl || "",
    flowchFields: Array.isArray(integration.flowchFields)
      ? integration.flowchFields
      : Array.isArray(integration?.data?.flowchFields)
        ? integration.data.flowchFields
        : [],
    flowchMappings: Array.isArray(integration.flowchMappings)
      ? integration.flowchMappings
      : Array.isArray(integration?.data?.flowchMappings)
        ? integration.data.flowchMappings
        : [],
  })).map(integration => ({
    ...integration,
    flowchMappings: Array.isArray(integration.flowchMappings)
      ? integration.flowchMappings.map(mapping => ({
          ...DEFAULT_FLOWCH_MAPPING,
          ...mapping,
          // Keep source always available and default to a Talk payload field.
          source: String(mapping?.source || "").trim() || "payload.body",
          // Enviar is implicit in UI; keep true for backward compatibility.
          send: true,
        }))
      : [],
  }));
};

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    color: "",
    greetingMessage: "",
    startAttendanceMessage: "",
    finishAttendanceMessage: "",
    allowManualAttendance: true,
    allowAiAgents: false,
    aiAssignmentMode: "disabled",
    defaultAiUserId: "",
    aiHandoffQueueId: "",
    outside24hTemplateName: "",
    outside24hTemplateLanguage: "pt_BR",
    outside24hAllowedTemplateNames: "",
    queueIntegrations: [],
  };

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const [activeTab, setActiveTab] = useState(0);
  const [contactOptions, setContactOptions] = useState([]);
  const [queueOptions, setQueueOptions] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [integrationTestLoading, setIntegrationTestLoading] = useState({});
  const [integrationTestResults, setIntegrationTestResults] = useState({});
  const greetingRef = useRef();

  const ensureContactsLoadedByIds = async contactIds => {
    if (!Array.isArray(contactIds) || !contactIds.length) {
      return;
    }

    const existingIds = new Set(contactOptions.map(contact => Number(contact.id)));
    const missingIds = contactIds
      .map(value => Number(value))
      .filter(value => Number.isInteger(value) && value > 0 && !existingIds.has(value));

    if (!missingIds.length) {
      return;
    }

    try {
      const responses = await Promise.all(
        missingIds.map(contactId => api.get(`/contacts/${contactId}`))
      );

      setContactOptions(prev => {
        const map = new Map(prev.map(item => [Number(item.id), item]));
        responses.forEach(({ data }) => {
          if (data?.id) {
            map.set(Number(data.id), data);
          }
        });
        return sortContacts(Array.from(map.values()));
      });
    } catch (_err) {
      // Keep form usable even if one contact no longer exists.
    }
  };

  const loadAllContacts = async () => {
    let pageNumber = 1;
    let hasMore = true;

    setContactsLoading(true);

    try {
      const map = new Map(contactOptions.map(item => [Number(item.id), item]));

      while (hasMore && pageNumber <= 100) {
        const { data } = await api.get("/contacts/", {
          params: {
            searchParam: "",
            pageNumber,
          },
        });

        const contacts = Array.isArray(data?.contacts) ? data.contacts : [];

        contacts.forEach(contact => {
          if (contact?.id) {
            map.set(Number(contact.id), contact);
          }
        });

        hasMore = Boolean(data?.hasMore);
        pageNumber += 1;
      }

      setContactOptions(sortContacts(Array.from(map.values())));
    } catch (err) {
      toastError(err);
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchQueues = async () => {
      try {
        const { data } = await api.get("/queue");
        setQueueOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
        setQueueOptions([]);
      }
    };

    fetchQueues();
  }, [open]);

  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);

        setQueue(prevState => {
          return {
            ...prevState,
            ...data,
            defaultAiUserId: data.defaultAiUserId || "",
            aiHandoffQueueId: data.aiHandoffQueueId || "",
            queueIntegrations: normalizeQueueIntegrations(data.queueIntegrations),
          };
        });
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue(initialState);
    };
  }, [queueId, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    loadAllContacts();
  }, [open]);

  useEffect(() => {
    if (!open || !Array.isArray(queue.queueIntegrations)) {
      return;
    }

    const allContactIds = queue.queueIntegrations.flatMap(integration =>
      Array.isArray(integration.contactIds) ? integration.contactIds : []
    );

    ensureContactsLoadedByIds(allContactIds);
  }, [open, queue.queueIntegrations]);

  const handleClose = () => {
    onClose();
    setQueue(initialState);
    setActiveTab(0);
    setIntegrationTestLoading({});
    setIntegrationTestResults({});
  };

  const handleTestIntegration = async (integrationIndex, values) => {
    const integration = values?.queueIntegrations?.[integrationIndex];

    if (!integration) {
      return;
    }

    const selectedEvent = Array.isArray(integration.triggerEvents)
      ? integration.triggerEvents[0] || "message_received"
      : "message_received";

    setIntegrationTestLoading(prev => ({
      ...prev,
      [integrationIndex]: true,
    }));

    try {
      const { data } = await api.post("/queue/integrations/test", {
        queueId: queueId || null,
        event: selectedEvent,
        integration,
      });

      setIntegrationTestResults(prev => ({
        ...prev,
        [integrationIndex]: data,
      }));

      toast.success(i18n.t("queueModal.integrations.test.messages.success"));
    } catch (err) {
      setIntegrationTestResults(prev => ({
        ...prev,
        [integrationIndex]: {
          success: false,
          error: err?.response?.data || err?.message || "Test request failed",
        },
      }));
      toastError(err);
    } finally {
      setIntegrationTestLoading(prev => ({
        ...prev,
        [integrationIndex]: false,
      }));
    }
  };

  const renderTestResult = (integrationIndex, values) => {
    const result = integrationTestResults[integrationIndex];

    return (
      <>
        <div className={classes.testActionRow}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleTestIntegration(integrationIndex, values)}
            disabled={Boolean(integrationTestLoading[integrationIndex])}
          >
            {i18n.t("queueModal.integrations.test.button")}
            {integrationTestLoading[integrationIndex] && (
              <CircularProgress
                size={18}
                style={{ marginLeft: 8 }}
              />
            )}
          </Button>
        </div>

        {result ? (
          <Paper variant="outlined" className={classes.testResultBox}>
            <Typography variant="subtitle2" className={classes.testResultTitle}>
              {i18n.t("queueModal.integrations.test.resultTitle")}
            </Typography>

            <Typography variant="caption" color="textSecondary" className={classes.testResultSubtitle}>
              {i18n.t("queueModal.integrations.test.requestData")}
            </Typography>
            <pre className={classes.testResultPre}>
              {JSON.stringify(result.request || null, null, 2)}
            </pre>

            <Typography variant="caption" color="textSecondary" className={classes.testResultSubtitle}>
              {i18n.t("queueModal.integrations.test.responseData")}
            </Typography>
            <pre className={classes.testResultPre}>
              {JSON.stringify(result.response || result.error || null, null, 2)}
            </pre>
          </Paper>
        ) : null}
      </>
    );
  };

  const handleSaveQueue = async values => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, values);
      } else {
        await api.post("/queue", values);
      }
      toast.success("Queue saved successfully");
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const integrationTypeOptions = [
    {
      value: "webhook",
      label: i18n.t("queueModal.integrations.types.webhook"),
    },
    {
      value: "flowch",
      label: i18n.t("queueModal.integrations.types.flowch"),
    },
  ];

  const loadFlowchDoc = async (index, values, setFieldValue) => {
    const endpointUrl = String(values.queueIntegrations[index]?.endpointUrl || "").trim();
    const token = String(values.queueIntegrations[index]?.token || "").trim();

    if (!endpointUrl) {
      toast.error(i18n.t("queueModal.integrations.flowch.messages.endpointRequired"));
      return;
    }

    try {
      const { data } = await api.post("/queue/integrations/flowch/load-doc", {
        endpointUrl,
        token,
      });

      const loadedFields = Array.isArray(data?.fields) ? data.fields : [];
      const currentMappings = values.queueIntegrations[index]?.flowchMappings || [];
      const mergedMappings = mergeFlowchMappings(currentMappings, loadedFields);

      setFieldValue(`queueIntegrations.${index}.endpointUrl`, data?.endpointUrl || endpointUrl);
      setFieldValue(`queueIntegrations.${index}.flowchDocUrl`, data?.docUrl || "");
      setFieldValue(`queueIntegrations.${index}.flowchFields`, loadedFields);
      setFieldValue(`queueIntegrations.${index}.flowchMappings`, mergedMappings);

      toast.success(i18n.t("queueModal.integrations.flowch.messages.docLoaded"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} scroll="paper" maxWidth="md" fullWidth>
        <DialogTitle>
          {queueId ? `${i18n.t("queueModal.title.edit")}` : `${i18n.t("queueModal.title.add")}`}
        </DialogTitle>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label={i18n.t("queueModal.tabs.basicData")} />
          <Tab label={i18n.t("queueModal.tabs.integrations")} />
        </Tabs>
        <Formik
          initialValues={queue}
          enableReinitialize={true}
          validationSchema={QueueSchema}
          onSubmit={async (values, actions) => {
            try {
              await handleSaveQueue(values);
            } finally {
              actions.setSubmitting(false);
            }
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => {
            return (
            <Form>
              <DialogContent dividers>
                <TabPanel value={activeTab} index={0}>
                  <div className={classes.basicDataRow}>
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.name")}
                      autoFocus
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      className={classes.nameField}
                    />
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.color")}
                      name="color"
                      id="color"
                      onFocus={() => {
                        setColorPickerModalOpen(true);
                        if (greetingRef.current) {
                          greetingRef.current.focus();
                        }
                      }}
                      error={touched.color && Boolean(errors.color)}
                      helperText={touched.color && errors.color}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              style={{ backgroundColor: values.color }}
                              className={classes.colorAdorment}
                            ></div>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => setColorPickerModalOpen(true)}
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                      variant="outlined"
                      margin="dense"
                      className={classes.colorField}
                    />
                  </div>
                  <ColorPicker
                    open={colorPickerModalOpen}
                    handleClose={() => setColorPickerModalOpen(false)}
                    onChange={color => {
                      setFieldValue("color", color);
                    }}
                  />
                  <div>
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.greetingMessage")}
                      type="greetingMessage"
                      multiline
                      inputRef={greetingRef}
                      rows={5}
                      fullWidth
                      name="greetingMessage"
                      error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                      helperText={touched.greetingMessage && errors.greetingMessage}
                      variant="outlined"
                      margin="dense"
                    />
                  </div>
                  <div>
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.startAttendanceMessage")}
                      type="startAttendanceMessage"
                      multiline
                      rows={3}
                      fullWidth
                      name="startAttendanceMessage"
                      error={
                        touched.startAttendanceMessage &&
                        Boolean(errors.startAttendanceMessage)
                      }
                      helperText={
                        touched.startAttendanceMessage && errors.startAttendanceMessage
                      }
                      variant="outlined"
                      margin="dense"
                    />
                  </div>
                  <div>
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.finishAttendanceMessage")}
                      type="finishAttendanceMessage"
                      multiline
                      rows={3}
                      fullWidth
                      name="finishAttendanceMessage"
                      error={
                        touched.finishAttendanceMessage &&
                        Boolean(errors.finishAttendanceMessage)
                      }
                      helperText={
                        touched.finishAttendanceMessage && errors.finishAttendanceMessage
                      }
                      variant="outlined"
                      margin="dense"
                    />
                  </div>
                  <div>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          color="primary"
                          checked={values.allowManualAttendance !== false}
                          onChange={event => {
                            setFieldValue("allowManualAttendance", event.target.checked);
                          }}
                        />
                      )}
                      label={i18n.t("queueModal.form.allowManualAttendance")}
                    />
                  </div>
                  <div>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          color="primary"
                          checked={values.allowAiAgents === true}
                          onChange={event => {
                            setFieldValue("allowAiAgents", event.target.checked);
                            setFieldValue(
                              "aiAssignmentMode",
                              event.target.checked ? "manual_only" : "disabled"
                            );
                            if (!event.target.checked) {
                              setFieldValue("defaultAiUserId", "");
                              setFieldValue("aiHandoffQueueId", "");
                            }
                          }}
                        />
                      )}
                      label={i18n.t("queueModal.form.allowAiAgents")}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("queueModal.form.allowAiAgentsHelp")}
                    </Typography>
                  </div>
                  {values.allowAiAgents === true && (
                    <div>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      >
                        <InputLabel id="ai-handoff-queue-label">
                          {i18n.t("queueModal.form.aiHandoffQueue")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("queueModal.form.aiHandoffQueue")}
                          labelId="ai-handoff-queue-label"
                          id="ai-handoff-queue"
                          name="aiHandoffQueueId"
                        >
                          <MenuItem value="">
                            {i18n.t("queueModal.form.aiHandoffQueueNone")}
                          </MenuItem>
                          {queueOptions
                            .filter(option => Number(option.id) !== Number(queueId))
                            .map(option => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                        </Field>
                        <FormHelperText>
                          {i18n.t("queueModal.form.aiHandoffQueueHelp")}
                        </FormHelperText>
                      </FormControl>
                    </div>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <FieldArray name="queueIntegrations">
                    {arrayHelpers => (
                      <>
                        {values.queueIntegrations && values.queueIntegrations.length > 0 ? (
                          values.queueIntegrations.map((integration, index) => {
                            const nameError = getIn(errors, `queueIntegrations.${index}.name`);
                            const nameTouched = getIn(touched, `queueIntegrations.${index}.name`);
                            const webhookUrlError = getIn(
                              errors,
                              `queueIntegrations.${index}.webhookUrl`
                            );
                            const webhookUrlTouched = getIn(
                              touched,
                              `queueIntegrations.${index}.webhookUrl`
                            );
                            const triggerEventsError = getIn(
                              errors,
                              `queueIntegrations.${index}.triggerEvents`
                            );
                            const triggerEventsTouched = getIn(
                              touched,
                              `queueIntegrations.${index}.triggerEvents`
                            );
                            const statusError = getIn(
                              errors,
                              `queueIntegrations.${index}.status`
                            );
                            const statusTouched = getIn(
                              touched,
                              `queueIntegrations.${index}.status`
                            );
                            const contactScopeError = getIn(
                              errors,
                              `queueIntegrations.${index}.contactScope`
                            );
                            const contactScopeTouched = getIn(
                              touched,
                              `queueIntegrations.${index}.contactScope`
                            );
                            const contactIdsError = getIn(
                              errors,
                              `queueIntegrations.${index}.contactIds`
                            );
                            const contactIdsTouched = getIn(
                              touched,
                              `queueIntegrations.${index}.contactIds`
                            );
                            const endpointUrlError = getIn(
                              errors,
                              `queueIntegrations.${index}.endpointUrl`
                            );
                            const endpointUrlTouched = getIn(
                              touched,
                              `queueIntegrations.${index}.endpointUrl`
                            );
                            const flowchMappingsError = getIn(
                              errors,
                              `queueIntegrations.${index}.flowchMappings`
                            );
                            const flowchMappingsTouched = getIn(
                              touched,
                              `queueIntegrations.${index}.flowchMappings`
                            );

                            return (
                              <Paper key={`integration-${index}`} className={classes.integrationItem} elevation={0}>
                                <div className={classes.integrationHeader}>
                                  <Typography variant="subtitle2">
                                    {i18n.t("queueModal.integrations.integrationLabel", {
                                      number: index + 1,
                                    })}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => arrayHelpers.remove(index)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </div>

                                <div className={classes.integrationFields}>
                                  <FormControl variant="outlined" margin="dense" fullWidth>
                                    <InputLabel id={`integration-type-label-${index}`}>
                                      {i18n.t("queueModal.integrations.form.type")}
                                    </InputLabel>
                                    <Select
                                      labelId={`integration-type-label-${index}`}
                                      value={integration.type}
                                      onChange={event => {
                                        setFieldValue(
                                          `queueIntegrations.${index}.type`,
                                          event.target.value
                                        );
                                      }}
                                      label={i18n.t("queueModal.integrations.form.type")}
                                    >
                                      {integrationTypeOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  {integration.type === "webhook" && (
                                    <>
                                      <Field
                                        as={TextField}
                                        label={i18n.t("queueModal.integrations.form.name")}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        name={`queueIntegrations.${index}.name`}
                                        error={Boolean(nameTouched && nameError)}
                                        helperText={nameTouched && nameError}
                                      />

                                      <Field
                                        as={TextField}
                                        label={i18n.t("queueModal.integrations.form.webhookUrl")}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        name={`queueIntegrations.${index}.webhookUrl`}
                                        error={Boolean(webhookUrlTouched && webhookUrlError)}
                                        helperText={webhookUrlTouched && webhookUrlError}
                                      />

                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(triggerEventsTouched && triggerEventsError)}
                                      >
                                        <InputLabel
                                          id={`integration-trigger-events-label-${index}`}
                                        >
                                          {i18n.t("queueModal.integrations.form.triggerEvents")}
                                        </InputLabel>
                                        <Select
                                          labelId={`integration-trigger-events-label-${index}`}
                                          multiple
                                          value={integration.triggerEvents || []}
                                          onChange={event => {
                                            setFieldValue(
                                              `queueIntegrations.${index}.triggerEvents`,
                                              event.target.value
                                            );
                                          }}
                                          input={
                                            <OutlinedInput
                                              label={i18n.t(
                                                "queueModal.integrations.form.triggerEvents"
                                              )}
                                            />
                                          }
                                          renderValue={selected => {
                                            const selectedEvents = Array.isArray(selected)
                                              ? selected
                                              : [];

                                            return selectedEvents
                                              .map(
                                                selectedValue => {
                                                  const option = WEBHOOK_EVENT_OPTIONS.find(
                                                    item => item.value === selectedValue
                                                  );

                                                  return option
                                                    ? i18n.t(option.labelKey)
                                                    : selectedValue;
                                                }
                                              )
                                              .join(", ");
                                          }}
                                        >
                                          {WEBHOOK_EVENT_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                              <Checkbox
                                                checked={
                                                  (integration.triggerEvents || []).indexOf(
                                                    option.value
                                                  ) > -1
                                                }
                                              />
                                              <ListItemText
                                                primary={i18n.t(option.labelKey)}
                                              />
                                            </MenuItem>
                                          ))}
                                        </Select>
                                        {triggerEventsTouched && triggerEventsError && (
                                          <FormHelperText>{triggerEventsError}</FormHelperText>
                                        )}
                                      </FormControl>

                                      <Field
                                        as={TextField}
                                        label={i18n.t("queueModal.integrations.form.token")}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        name={`queueIntegrations.${index}.token`}
                                      />

                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(contactScopeTouched && contactScopeError)}
                                      >
                                        <InputLabel id={`integration-contact-scope-label-${index}`}>
                                          {i18n.t("queueModal.integrations.form.contact")}
                                        </InputLabel>
                                        <Select
                                          labelId={`integration-contact-scope-label-${index}`}
                                          value={values.queueIntegrations[index]?.contactScope || "all"}
                                          onChange={event => {
                                            const value = event.target.value;
                                            setFieldValue(
                                              `queueIntegrations.${index}.contactScope`,
                                              value
                                            );

                                            if (value !== "specific") {
                                              setFieldValue(
                                                `queueIntegrations.${index}.contactIds`,
                                                []
                                              );
                                            }
                                          }}
                                          input={
                                            <OutlinedInput
                                              label={i18n.t("queueModal.integrations.form.contact")}
                                            />
                                          }
                                        >
                                          <MenuItem value="all">
                                            {i18n.t("queueModal.integrations.contactScope.all")}
                                          </MenuItem>
                                          <MenuItem value="specific">
                                            {i18n.t("queueModal.integrations.contactScope.specific")}
                                          </MenuItem>
                                        </Select>
                                        {contactScopeTouched && contactScopeError && (
                                          <FormHelperText>{contactScopeError}</FormHelperText>
                                        )}
                                      </FormControl>

                                      {(values.queueIntegrations[index]?.contactScope || "all") === "specific" && (
                                        <Autocomplete
                                          multiple
                                          options={contactOptions}
                                          loading={contactsLoading}
                                          value={(values.queueIntegrations[index]?.contactIds || []).map(contactId => {
                                            const found = contactOptions.find(
                                              option => Number(option.id) === Number(contactId)
                                            );

                                            if (found) {
                                              return found;
                                            }

                                            return {
                                              id: contactId,
                                              name: `#${contactId}`,
                                              number: "",
                                            };
                                          })}
                                          getOptionLabel={option =>
                                            formatContactLabel(option)
                                          }
                                          filterOptions={(options, state) => {
                                            const rawInput = String(state.inputValue || "");
                                            const normalizedInput = normalizeSearchText(rawInput);
                                            const digitsInput = onlyDigits(rawInput);

                                            if (!normalizedInput && !digitsInput) {
                                              return options;
                                            }

                                            return options.filter(option => {
                                              const name = normalizeSearchText(option?.name);
                                              const number = String(option?.number || "");
                                              const normalizedNumber = normalizeSearchText(number);
                                              const digitsNumber = onlyDigits(number);

                                              const byName =
                                                normalizedInput && name.includes(normalizedInput);
                                              const byNumberText =
                                                normalizedInput &&
                                                normalizedNumber.includes(normalizedInput);
                                              const byNumberDigits =
                                                digitsInput && digitsNumber.includes(digitsInput);

                                              return byName || byNumberText || byNumberDigits;
                                            });
                                          }}
                                          getOptionSelected={(option, value) =>
                                            Number(option.id) === Number(value.id)
                                          }
                                          onChange={(_, selectedContacts) => {
                                            setFieldValue(
                                              `queueIntegrations.${index}.contactIds`,
                                              selectedContacts
                                                .map(contact => Number(contact.id))
                                                .filter(
                                                  value =>
                                                    Number.isInteger(value) && value > 0
                                                )
                                            );
                                          }}
                                          filterSelectedOptions
                                          renderInput={params => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              margin="dense"
                                              label={i18n.t(
                                                "queueModal.integrations.form.specificContacts"
                                              )}
                                              placeholder={i18n.t(
                                                "queueModal.integrations.form.searchContactPlaceholder"
                                              )}
                                              error={Boolean(
                                                contactIdsTouched && contactIdsError
                                              )}
                                              helperText={
                                                contactIdsTouched && contactIdsError
                                                  ? contactIdsError
                                                  : ""
                                              }
                                            />
                                          )}
                                        />
                                      )}

                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(statusTouched && statusError)}
                                      >
                                        <InputLabel id={`integration-status-label-${index}`}>
                                          {i18n.t("queueModal.integrations.form.status")}
                                        </InputLabel>
                                        <Select
                                          labelId={`integration-status-label-${index}`}
                                          value={integration.status || "active"}
                                          onChange={event => {
                                            setFieldValue(
                                              `queueIntegrations.${index}.status`,
                                              event.target.value
                                            );
                                          }}
                                          label={i18n.t("queueModal.integrations.form.status")}
                                        >
                                          <MenuItem value="active">
                                            {i18n.t("queueModal.integrations.status.active")}
                                          </MenuItem>
                                          <MenuItem value="inactive">
                                            {i18n.t("queueModal.integrations.status.inactive")}
                                          </MenuItem>
                                        </Select>
                                        {statusTouched && statusError && (
                                          <FormHelperText>{statusError}</FormHelperText>
                                        )}
                                      </FormControl>

                                      {renderTestResult(index, values)}
                                    </>
                                  )}

                                  {integration.type === "flowch" && (
                                    <>
                                      <Field
                                        as={TextField}
                                        label={i18n.t("queueModal.integrations.form.name")}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        name={`queueIntegrations.${index}.name`}
                                        error={Boolean(nameTouched && nameError)}
                                        helperText={nameTouched && nameError}
                                      />

                                      <Field
                                        as={TextField}
                                        label={i18n.t("queueModal.integrations.flowch.form.endpointUrl")}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        name={`queueIntegrations.${index}.endpointUrl`}
                                        error={Boolean(endpointUrlTouched && endpointUrlError)}
                                        helperText={endpointUrlTouched && endpointUrlError}
                                      />

                                      <Field
                                        as={TextField}
                                        label={i18n.t("queueModal.integrations.form.token")}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        name={`queueIntegrations.${index}.token`}
                                      />

                                      <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => loadFlowchDoc(index, values, setFieldValue)}
                                      >
                                        {i18n.t("queueModal.integrations.flowch.buttons.loadDoc")}
                                      </Button>

                                      {values.queueIntegrations[index]?.flowchDocUrl ? (
                                        <Typography variant="caption" color="textSecondary">
                                          {i18n.t("queueModal.integrations.flowch.form.docUrl")}: {values.queueIntegrations[index]?.flowchDocUrl}
                                        </Typography>
                                      ) : null}

                                      <Typography variant="subtitle2">
                                        {i18n.t("queueModal.integrations.flowch.form.fieldMappings")}
                                      </Typography>

                                      <FieldArray name={`queueIntegrations.${index}.flowchMappings`}>
                                        {flowchArrayHelpers => (
                                          <>
                                            {(values.queueIntegrations[index]?.flowchMappings || []).map((mapping, mappingIndex) => {
                                              const mappingFieldNameError = getIn(
                                                errors,
                                                `queueIntegrations.${index}.flowchMappings.${mappingIndex}.fieldName`
                                              );
                                              const mappingFieldNameTouched = getIn(
                                                touched,
                                                `queueIntegrations.${index}.flowchMappings.${mappingIndex}.fieldName`
                                              );
                                              const mappingSourceError = getIn(
                                                errors,
                                                `queueIntegrations.${index}.flowchMappings.${mappingIndex}.source`
                                              );
                                              const mappingSourceTouched = getIn(
                                                touched,
                                                `queueIntegrations.${index}.flowchMappings.${mappingIndex}.source`
                                              );
                                              const mappingFixedError = getIn(
                                                errors,
                                                `queueIntegrations.${index}.flowchMappings.${mappingIndex}.fixedValue`
                                              );
                                              const mappingFixedTouched = getIn(
                                                touched,
                                                `queueIntegrations.${index}.flowchMappings.${mappingIndex}.fixedValue`
                                              );

                                              const mappingValueMode = mapping.source === "fixed" ? "fixed" : "talk";

                                              return (
                                                <Paper
                                                  key={`flowch-mapping-${index}-${mappingIndex}`}
                                                  variant="outlined"
                                                  style={{ padding: 12, marginBottom: 8 }}
                                                >
                                                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => flowchArrayHelpers.remove(mappingIndex)}
                                                    >
                                                      <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                  </div>

                                                  <Field
                                                    as={TextField}
                                                    label={i18n.t("queueModal.integrations.flowch.form.fieldName")}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                    name={`queueIntegrations.${index}.flowchMappings.${mappingIndex}.fieldName`}
                                                    error={Boolean(mappingFieldNameTouched && mappingFieldNameError)}
                                                    helperText={mappingFieldNameTouched && mappingFieldNameError}
                                                  />

                                                  <Field
                                                    as={TextField}
                                                    label={i18n.t("queueModal.integrations.flowch.form.fieldLabel")}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                    name={`queueIntegrations.${index}.flowchMappings.${mappingIndex}.label`}
                                                  />

                                                  <FormControl
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                  >
                                                    <InputLabel>
                                                      {i18n.t("queueModal.integrations.flowch.form.valueMode")}
                                                    </InputLabel>
                                                    <Select
                                                      value={mappingValueMode}
                                                      onChange={event => {
                                                        const selectedMode = event.target.value;
                                                        setFieldValue(
                                                          `queueIntegrations.${index}.flowchMappings.${mappingIndex}.source`,
                                                          selectedMode === "fixed" ? "fixed" : "payload.body"
                                                        );

                                                        if (selectedMode !== "fixed") {
                                                          setFieldValue(
                                                            `queueIntegrations.${index}.flowchMappings.${mappingIndex}.fixedValue`,
                                                            ""
                                                          );
                                                        }
                                                      }}
                                                      label={i18n.t("queueModal.integrations.flowch.form.valueMode")}
                                                    >
                                                      <MenuItem value="talk">
                                                        {i18n.t("queueModal.integrations.flowch.valueMode.talk")}
                                                      </MenuItem>
                                                      <MenuItem value="fixed">
                                                        {i18n.t("queueModal.integrations.flowch.valueMode.fixed")}
                                                      </MenuItem>
                                                    </Select>
                                                  </FormControl>

                                                  {mappingValueMode === "talk" ? (
                                                    <FormControl
                                                      variant="outlined"
                                                      margin="dense"
                                                      fullWidth
                                                      error={Boolean(mappingSourceTouched && mappingSourceError)}
                                                    >
                                                      <InputLabel>
                                                        {i18n.t("queueModal.integrations.flowch.form.source")}
                                                      </InputLabel>
                                                      <Select
                                                        value={mapping.source || "payload.body"}
                                                        onChange={event => {
                                                          setFieldValue(
                                                            `queueIntegrations.${index}.flowchMappings.${mappingIndex}.source`,
                                                            event.target.value
                                                          );
                                                        }}
                                                        label={i18n.t("queueModal.integrations.flowch.form.source")}
                                                      >
                                                        {FLOWCH_TALK_SOURCE_OPTIONS.map(sourceOption => (
                                                          <MenuItem key={sourceOption.value} value={sourceOption.value}>
                                                            {i18n.t(sourceOption.labelKey)}
                                                          </MenuItem>
                                                        ))}
                                                      </Select>
                                                      {mappingSourceTouched && mappingSourceError && (
                                                        <FormHelperText>{mappingSourceError}</FormHelperText>
                                                      )}
                                                    </FormControl>
                                                  ) : (
                                                    <Field
                                                      as={TextField}
                                                      label={i18n.t("queueModal.integrations.flowch.form.fixedValue")}
                                                      variant="outlined"
                                                      margin="dense"
                                                      fullWidth
                                                      name={`queueIntegrations.${index}.flowchMappings.${mappingIndex}.fixedValue`}
                                                      error={Boolean(mappingFixedTouched && mappingFixedError)}
                                                      helperText={mappingFixedTouched && mappingFixedError}
                                                    />
                                                  )}
                                                </Paper>
                                              );
                                            })}

                                            <Button
                                              variant="outlined"
                                              color="primary"
                                              startIcon={<AddIcon />}
                                              onClick={() =>
                                                flowchArrayHelpers.push({ ...DEFAULT_FLOWCH_MAPPING })
                                              }
                                              fullWidth
                                            >
                                              {i18n.t("queueModal.integrations.flowch.buttons.addField")}
                                            </Button>
                                          </>
                                        )}
                                      </FieldArray>

                                      {flowchMappingsTouched && flowchMappingsError && (
                                        <FormHelperText error>{String(flowchMappingsError)}</FormHelperText>
                                      )}

                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(triggerEventsTouched && triggerEventsError)}
                                      >
                                        <InputLabel id={`integration-trigger-events-flowch-label-${index}`}>
                                          {i18n.t("queueModal.integrations.form.triggerEvents")}
                                        </InputLabel>
                                        <Select
                                          labelId={`integration-trigger-events-flowch-label-${index}`}
                                          multiple
                                          value={integration.triggerEvents || []}
                                          onChange={event => {
                                            setFieldValue(
                                              `queueIntegrations.${index}.triggerEvents`,
                                              event.target.value
                                            );
                                          }}
                                          input={
                                            <OutlinedInput
                                              label={i18n.t("queueModal.integrations.form.triggerEvents")}
                                            />
                                          }
                                          renderValue={selected => {
                                            const selectedEvents = Array.isArray(selected)
                                              ? selected
                                              : [];

                                            return selectedEvents
                                              .map(selectedValue => {
                                                const option = WEBHOOK_EVENT_OPTIONS.find(
                                                  item => item.value === selectedValue
                                                );

                                                return option ? i18n.t(option.labelKey) : selectedValue;
                                              })
                                              .join(", ");
                                          }}
                                        >
                                          {WEBHOOK_EVENT_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                              <Checkbox
                                                checked={
                                                  (integration.triggerEvents || []).indexOf(option.value) > -1
                                                }
                                              />
                                              <ListItemText primary={i18n.t(option.labelKey)} />
                                            </MenuItem>
                                          ))}
                                        </Select>
                                        {triggerEventsTouched && triggerEventsError && (
                                          <FormHelperText>{triggerEventsError}</FormHelperText>
                                        )}
                                      </FormControl>

                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(contactScopeTouched && contactScopeError)}
                                      >
                                        <InputLabel id={`integration-contact-scope-flowch-label-${index}`}>
                                          {i18n.t("queueModal.integrations.form.contact")}
                                        </InputLabel>
                                        <Select
                                          labelId={`integration-contact-scope-flowch-label-${index}`}
                                          value={values.queueIntegrations[index]?.contactScope || "all"}
                                          onChange={event => {
                                            const value = event.target.value;
                                            setFieldValue(
                                              `queueIntegrations.${index}.contactScope`,
                                              value
                                            );

                                            if (value !== "specific") {
                                              setFieldValue(
                                                `queueIntegrations.${index}.contactIds`,
                                                []
                                              );
                                            }
                                          }}
                                          input={
                                            <OutlinedInput label={i18n.t("queueModal.integrations.form.contact")} />
                                          }
                                        >
                                          <MenuItem value="all">
                                            {i18n.t("queueModal.integrations.contactScope.all")}
                                          </MenuItem>
                                          <MenuItem value="specific">
                                            {i18n.t("queueModal.integrations.contactScope.specific")}
                                          </MenuItem>
                                        </Select>
                                        {contactScopeTouched && contactScopeError && (
                                          <FormHelperText>{contactScopeError}</FormHelperText>
                                        )}
                                      </FormControl>

                                      {(values.queueIntegrations[index]?.contactScope || "all") === "specific" && (
                                        <Autocomplete
                                          multiple
                                          options={contactOptions}
                                          loading={contactsLoading}
                                          value={(values.queueIntegrations[index]?.contactIds || []).map(contactId => {
                                            const found = contactOptions.find(
                                              option => Number(option.id) === Number(contactId)
                                            );

                                            if (found) {
                                              return found;
                                            }

                                            return {
                                              id: contactId,
                                              name: `#${contactId}`,
                                              number: "",
                                            };
                                          })}
                                          getOptionLabel={option => formatContactLabel(option)}
                                          filterOptions={(options, state) => {
                                            const rawInput = String(state.inputValue || "");
                                            const normalizedInput = normalizeSearchText(rawInput);
                                            const digitsInput = onlyDigits(rawInput);

                                            if (!normalizedInput && !digitsInput) {
                                              return options;
                                            }

                                            return options.filter(option => {
                                              const name = normalizeSearchText(option?.name);
                                              const number = String(option?.number || "");
                                              const normalizedNumber = normalizeSearchText(number);
                                              const digitsNumber = onlyDigits(number);

                                              const byName = normalizedInput && name.includes(normalizedInput);
                                              const byNumberText =
                                                normalizedInput && normalizedNumber.includes(normalizedInput);
                                              const byNumberDigits =
                                                digitsInput && digitsNumber.includes(digitsInput);

                                              return byName || byNumberText || byNumberDigits;
                                            });
                                          }}
                                          getOptionSelected={(option, value) =>
                                            Number(option.id) === Number(value.id)
                                          }
                                          onChange={(_, selectedContacts) => {
                                            setFieldValue(
                                              `queueIntegrations.${index}.contactIds`,
                                              selectedContacts
                                                .map(contact => Number(contact.id))
                                                .filter(value => Number.isInteger(value) && value > 0)
                                            );
                                          }}
                                          filterSelectedOptions
                                          renderInput={params => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              margin="dense"
                                              label={i18n.t("queueModal.integrations.form.specificContacts")}
                                              placeholder={i18n.t(
                                                "queueModal.integrations.form.searchContactPlaceholder"
                                              )}
                                              error={Boolean(contactIdsTouched && contactIdsError)}
                                              helperText={
                                                contactIdsTouched && contactIdsError ? contactIdsError : ""
                                              }
                                            />
                                          )}
                                        />
                                      )}

                                      <FormControl
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(statusTouched && statusError)}
                                      >
                                        <InputLabel id={`integration-status-flowch-label-${index}`}>
                                          {i18n.t("queueModal.integrations.form.status")}
                                        </InputLabel>
                                        <Select
                                          labelId={`integration-status-flowch-label-${index}`}
                                          value={integration.status || "active"}
                                          onChange={event => {
                                            setFieldValue(
                                              `queueIntegrations.${index}.status`,
                                              event.target.value
                                            );
                                          }}
                                          label={i18n.t("queueModal.integrations.form.status")}
                                        >
                                          <MenuItem value="active">
                                            {i18n.t("queueModal.integrations.status.active")}
                                          </MenuItem>
                                          <MenuItem value="inactive">
                                            {i18n.t("queueModal.integrations.status.inactive")}
                                          </MenuItem>
                                        </Select>
                                        {statusTouched && statusError && (
                                          <FormHelperText>{statusError}</FormHelperText>
                                        )}
                                      </FormControl>

                                      {renderTestResult(index, values)}
                                    </>
                                  )}
                                </div>
                              </Paper>
                            );
                          })
                        ) : (
                          <Typography className={classes.emptyIntegrations}>
                            {i18n.t("queueModal.integrations.empty")}
                          </Typography>
                        )}

                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => arrayHelpers.push({ ...DEFAULT_INTEGRATION })}
                          className={classes.addIntegrationBtn}
                          fullWidth
                        >
                          {i18n.t("queueModal.integrations.buttons.add")}
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </TabPanel>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                  className={classes.actionButton}
                >
                  {i18n.t("queueModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  disableElevation
                  className={`${classes.btnWrapper} ${classes.actionButton}`}
                >
                  {isSubmitting
                    ? i18n.t("queueModal.buttons.saving")
                    : queueId
                    ? `${i18n.t("queueModal.buttons.okEdit")}`
                    : `${i18n.t("queueModal.buttons.okAdd")}`}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QueueModal;
